import { IconClose } from "@tine/designsystem-icons/sharp";
import { Button } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import {
   type ForwardedRef,
   forwardRef,
   type PropsWithChildren,
   type ReactNode,
   useEffect,
   useImperativeHandle,
   useRef
} from "react";
import { createPortal } from "react-dom";

import useOutsideClick from "../../../common/useOutsideClick";
import { focusableElementCssSelector } from "../../../common/utils";

type SlideFromSideProps = PropsWithChildren & {
   renderHeader: () => ReactNode;
   renderFooter?: () => ReactNode;
   show: boolean;
   side?: "left" | "right";
   onHide: () => void;
};

const commonBorderClassName = cn("border-0 border-solid print:hidden");
const rightBorderClassName = cn("border-r border-r-ink-border-subtle", commonBorderClassName);

const leftBorderClassName = cn("border-l border-l-ink-border-subtle", commonBorderClassName);

const commonClassName = cn("fixed bottom-0 top-0 z-20", "transition duration-300 ease-in-out", "z-50 bg-base-0", "flex flex-col");

const slideFromSideClassName = {
   left: cn(commonClassName, "left-0", rightBorderClassName),
   right: cn(commonClassName, "right-0", leftBorderClassName)
};

const visibleClassName = cn("translate-x-0");

const invisibleClassName = { left: cn("-translate-x-full"), right: cn("translate-x-full") };

const headerContainerClassName = cn("flex", "justify-between", "items-center", "m-3");
const bodyContainerClass = cn("m-3", "overflow-y-scroll", "grow", "relative");

const SlideFromSideContainer = forwardRef(
   (
      { show, onHide, renderHeader, renderFooter, side = "left", children }: SlideFromSideProps,
      forwardedRef: ForwardedRef<HTMLDivElement>
   ) => {
      const ref = useRef<HTMLDivElement>(null);
      useOutsideClick([ref], () => {
         onHide();
      });

      useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(forwardedRef, () => ref.current);

      useEffect(() => {
         const bodyElement = document.body;
         const bodyClasses = ["fixed", "overflow-hidden", "top-0", "bottom-0", "left-0", "right-0"];
         if (show) {
            bodyElement.classList.add(...bodyClasses);
         } else {
            bodyElement.classList.remove(...bodyClasses);
         }
      }, [show]);

      useEffect(() => {
         const keydownHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
               onHide();
            }
         };
         if (show) {
            document.addEventListener("keydown", keydownHandler);
         } else {
            document.removeEventListener("keydown", keydownHandler);
         }
         return () => removeEventListener("keydown", keydownHandler);
      }, [show]);

      useEffect(() => {
         if (show && ref && typeof ref !== "function") {
            const firstFocusable: HTMLElement = ref.current?.querySelector(focusableElementCssSelector) as HTMLElement;
            firstFocusable?.focus();
         }
      }, [show, ref]);

      const className = cn(slideFromSideClassName[side], show ? visibleClassName : invisibleClassName[side]);
      return (
         <div className={className} ref={ref}>
            <div className={headerContainerClassName}>
               <div>{renderHeader()}</div>
               <div>
                  <Button variant="tertiary" icon={<IconClose />} onClick={() => onHide()} />
               </div>
            </div>
            <div className={bodyContainerClass}>{children}</div>
            {renderFooter ? renderFooter() : null}
         </div>
      );
   }
);
const backdropClassName = cn("fixed inset-0 z-40 bg-base-900 opacity-50");

const Backdrop = () => {
   return <div className={backdropClassName} />;
};

const SlideFromSide = forwardRef(({ children, show, ...props }: SlideFromSideProps, _ref: ForwardedRef<HTMLDivElement>) => {
   return (
      <>
         {createPortal(
            <>
               {show && <Backdrop />}
               <SlideFromSideContainer show={show} {...props}>
                  {children}
               </SlideFromSideContainer>
            </>,
            document.body
         )}
      </>
   );
});

export default SlideFromSide;
